<template>
  <div class="bg-gray-800 p-4 rounded-lg mb-6">
    <form @submit.prevent="applyFilters">
      <!-- Search input -->
      <div class="mb-4">
        <label for="search" class="block text-sm font-medium text-gray-300 mb-1"
          >Pretraga po imenu</label
        >
        <input
          type="text"
          id="search"
          v-model="filters.search"
          placeholder="Unesite ime proizvoda..."
          class="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
          @keydown.enter="applyFilters"
          autocomplete="off"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <!-- Category filter - Zamenjen sa custom dropdown komponentom -->
        <CategoryDropdown v-model="filters.categoryId" />

        <!-- City filter - only show if not in hideFields -->
        <div v-if="!hideFields.includes('city')">
          <!-- Zamena sa novom CityDropdown komponentom -->
          <CityDropdown
            id="city-search"
            v-model="filters.city"
            label="Mesto"
            placeholder="Pretraži gradove..."
          />
        </div>

        <!-- Min price -->
        <div>
          <label
            for="minPrice"
            class="block text-sm font-medium text-gray-300 mb-1"
            >Min. cena</label
          >
          <input
            type="number"
            id="minPrice"
            v-model.number="filters.minPrice"
            min="0"
            placeholder="Min. cena"
            class="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            autocomplete="off"
          />
        </div>

        <!-- Max price -->
        <div>
          <label
            for="maxPrice"
            class="block text-sm font-medium text-gray-300 mb-1"
            >Max. cena</label
          >
          <input
            type="number"
            id="maxPrice"
            v-model.number="filters.maxPrice"
            min="0"
            placeholder="Max. cena"
            class="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          @click="resetFilters"
          class="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Obriši filtere
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Pretraži
        </button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  watch,
  onBeforeUnmount,
  PropType,
} from 'vue';
// Uvoz custom dropdown komponente
import CategoryDropdown from '@/components/CategoryDropdown.vue';
// Uvoz nove CityDropdown komponente
import CityDropdown from '@/components/CityDropdown.vue';

interface Filters {
  search: string;
  categoryId: string;
  city: string;
  minPrice: number | null;
  maxPrice: number | null;
}

export default defineComponent({
  name: 'ProductsSearchComponent',
  components: {
    CategoryDropdown,
    CityDropdown, // Registruj novu komponentu
  },
  emits: ['filter-change'],
  props: {
    initialFilters: {
      type: Object as PropType<Partial<Filters>>,
      default: () => ({}),
    },
    hideFields: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const filters = reactive<Filters>({
      search: props.initialFilters.search || '',
      categoryId: props.initialFilters.categoryId || '',
      city: props.initialFilters.city || '',
      minPrice:
        props.initialFilters.minPrice !== undefined
          ? props.initialFilters.minPrice
          : null,
      maxPrice:
        props.initialFilters.maxPrice !== undefined
          ? props.initialFilters.maxPrice
          : null,
    });

    // Primena filtera
    const applyFilters = () => {
      // Create a clean version of filters without null values and hidden fields
      const cleanFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (
            value !== null &&
            value !== '' &&
            !props.hideFields.includes(key)
          ) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      emit('filter-change', cleanFilters);
    };

    // Resetovanje filtera
    const resetFilters = () => {
      filters.search = '';
      filters.categoryId = '';

      // Only reset city if it's not hidden
      if (!props.hideFields.includes('city')) {
        filters.city = '';
      }

      filters.minPrice = null;
      filters.maxPrice = null;

      emit('filter-change', {});
    };

    // Ažuriranje filtera iz spoljnih izvora
    const updateFilters = (newFilters: Partial<Filters>) => {
      filters.search = newFilters.search || '';
      filters.categoryId = newFilters.categoryId || '';

      // Only update city if it's not hidden
      if (!props.hideFields.includes('city')) {
        filters.city = newFilters.city || '';
      }

      filters.minPrice =
        newFilters.minPrice !== undefined ? newFilters.minPrice : null;
      filters.maxPrice =
        newFilters.maxPrice !== undefined ? newFilters.maxPrice : null;
    };

    // Lifecycle hooks
    onMounted(() => {
      // Set initial values from props
      if (props.initialFilters.city) {
        filters.city = props.initialFilters.city;
      }
    });

    return {
      filters,
      applyFilters,
      resetFilters,
      updateFilters,
      hideFields: props.hideFields,
    };
  },
});
</script>
